// src/pages/lab/protocols/components/stepEditingComponents/StepResultsEditor.tsx
import React, { useState } from "react";
import { Checkbox, Select, InputNumber, Space, Badge, Collapse, Button, Table, Drawer, Input, message } from "antd";
import { UploadOutlined, SettingOutlined, DatabaseOutlined, PlusOutlined, DeleteOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";

import { Protocol, ProtocolStep } from "../../../../../../types/lab/ProtocolTypes";
import { ResultTypeOptions } from "../../../../../../types/lab/SampleResultTypes";


interface StepResultsEditorProps {
	step: Partial<ProtocolStep>;
	onStepChange: (step: Partial<ProtocolStep>) => void;
	disabled?: boolean;
	protocol: Partial<Protocol>;
}

// Result configuration interface
interface ResultConfiguration {
	result_types: string[];
	auto_analyze: boolean;
	requires_verification: boolean;
	file_restrictions?: {
		allowed_extensions: string[];
		max_file_size: number;
		min_files?: number;
		max_files?: number;
	};
	custom_fields?: Array<{
		name: string;
		type: string;
		required: boolean;
		options?: string[];
	}>;
}

const defaultResultConfig: ResultConfiguration = {
	result_types: ["file_upload"],
	auto_analyze: true,
	requires_verification: true,
	file_restrictions: {
		allowed_extensions: ["pdf", "csv", "xlsx", "txt"],
		max_file_size: 50,
		min_files: 1,
		max_files: 10,
	},
	custom_fields: [],
};

const StepResultsEditor: React.FC<StepResultsEditorProps> = ({ step, onStepChange, disabled = false, protocol }) => {
	const [activeCollapse, setActiveCollapse] = useState<string[]>(["1"]);
	const [customFieldDrawerVisible, setCustomFieldDrawerVisible] = useState(false);
	const [editingCustomField, setEditingCustomField] = useState<any>({
		name: "",
		type: "text",
		required: false,
		options: [],
	});

	// Get result configuration from step metadata
	const getResultConfig = (): ResultConfiguration => {
		const config = step.configuration as any;
		return config?.result_configuration || defaultResultConfig;
	};

	const updateResultConfig = (updates: Partial<ResultConfiguration>) => {
		const currentConfig = getResultConfig();
		const newConfig = { ...currentConfig, ...updates };

		const updatedStep = {
			...step,
			configuration: {
				...step.configuration,
				result_configuration: newConfig,
			},
		};
		onStepChange(updatedStep);
	};

	const resultConfig = getResultConfig();

	// gene_codes and gsa_raw are fixed-format uploads: no file restrictions or custom fields apply
	const isFixedFormatUpload = ["gene_codes", "gsa_raw"].includes(resultConfig.result_types[0]);

	// File type presets
	const fileTypePresets = [
		{
			name: "Lab Reports",
			extensions: ["pdf", "doc", "docx"],
			description: "Standard lab report documents",
		},
		{
			name: "Data Files",
			extensions: ["csv", "xlsx", "txt", "json"],
			description: "Raw data and structured files",
		},
		{
			name: "Images",
			extensions: ["jpg", "jpeg", "png", "tiff"],
			description: "Gel images, microscopy, charts",
		},
		{
			name: "Genomic Data",
			extensions: ["vcf", "bam", "fastq", "bed"],
			description: "NGS and genomic analysis files",
		},
	];

	const customFieldTypes = [
		{ label: "Text", value: "text" },
		{ label: "Number", value: "number" },
		{ label: "Dropdown", value: "dropdown" },
		{ label: "Date", value: "date" },
		{ label: "Boolean", value: "boolean" },
	];

	const handleAddCustomField = () => {
		setEditingCustomField({
			name: "",
			type: "text",
			required: false,
			options: [],
		});
		setCustomFieldDrawerVisible(true);
	};

	const handleSaveCustomField = () => {
		if (!editingCustomField.name.trim()) {
			message.error("Field name is required");
			return;
		}

		const currentFields = resultConfig.custom_fields || [];
		const updatedFields = [...currentFields, editingCustomField];

		updateResultConfig({
			custom_fields: updatedFields,
		});

		setCustomFieldDrawerVisible(false);
		setEditingCustomField({
			name: "",
			type: "text",
			required: false,
			options: [],
		});
		message.success("Custom field added");
	};

	const handleDeleteCustomField = (index: number) => {
		const currentFields = resultConfig.custom_fields || [];
		const updatedFields = currentFields.filter((_, i) => i !== index);

		updateResultConfig({
			custom_fields: updatedFields,
		});
		message.success("Custom field deleted");
	};

	const applyFileTypePreset = (preset: any) => {
		updateResultConfig({
			file_restrictions: {
				...resultConfig.file_restrictions,
				allowed_extensions: preset.extensions,
				max_file_size: resultConfig.file_restrictions?.max_file_size ?? 10,
			},
		});
		message.success(`Applied ${preset.name} preset`);
	};

	// Custom field table columns
	const customFieldColumns = [
		{
			title: "Field Name",
			dataIndex: "name",
			key: "name",
			render: (text: string) => (
				<code
					style={{
						fontSize: "11px",
						fontFamily: "monospace",
						backgroundColor: "rgba(0, 0, 0, 0.06)",
						padding: "2px 6px",
						borderRadius: "4px",
					}}
				>
					{text}
				</code>
			),
		},
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			width: 80,
			render: (type: string) => <Badge count={customFieldTypes.find((t) => t.value === type)?.label || type} style={{ backgroundColor: "#1890ff", fontSize: "10px" }} />,
		},
		{
			title: "Required",
			dataIndex: "required",
			key: "required",
			width: 70,
			render: (required: boolean) => <div style={{ textAlign: "center" }}>{required ? <span style={{ color: "#52c41a" }}>✓</span> : <span style={{ color: "var(--text-color-secondary)" }}>—</span>}</div>,
		},
		{
			title: "Actions",
			key: "actions",
			width: 60,
			render: (_: any, __: any, index: number) => <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteCustomField(index)} disabled={disabled} />,
		},
	];

	return (
		<div>
			{/* Header */}
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
				<Space size={4}>
					<UploadOutlined style={{ fontSize: "12px" }} />
					<span style={{ fontSize: "12px", fontWeight: 500 }}>Result Upload Configuration</span>
				</Space>
			</div>

			<Collapse
				activeKey={activeCollapse}
				onChange={(keys) => setActiveCollapse(keys as string[])}
				size="small"
				items={[
					{
						key: "1",
						label: (
							<Space size={4}>
								<SettingOutlined style={{ fontSize: "11px" }} />
								<span style={{ fontSize: "11px", fontWeight: 500 }}>Basic Settings</span>
							</Space>
						),
						children: (
							<Space direction="vertical" size="middle" style={{ width: "100%" }}>
								<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
									<div>
										<label style={{ display: "block", marginBottom: 8, fontSize: "11px", fontWeight: 500 }}>Accepted Result Types</label>
										<Space direction="vertical" size={8}>
											{ResultTypeOptions.map((option) => {
												// gene_codes and gsa_raw only make sense on a gsa_3 protocol
												if (protocol.test_types && !protocol.test_types.includes("gsa_3")) {
													if (option.value === "gene_codes" || option.value === "gsa_raw") return null;
												}

												return (
													<Checkbox
														key={option.value}
														checked={resultConfig.result_types[0] === option.value}
														onChange={(e) => {
															const updatedTypes = e.target.checked ? [option.value] : [];
															updateResultConfig({ result_types: updatedTypes });
														}}
														disabled={disabled}
													>
														<span style={{ fontSize: "11px" }}>{option.label}</span>
													</Checkbox>
												);
											})}
										</Space>
									</div>

									<div>
										<Space direction="vertical" size={12}>
											<Checkbox checked={resultConfig.auto_analyze} onChange={(e) => updateResultConfig({ auto_analyze: e.target.checked })} disabled={disabled}>
												<span style={{ fontSize: "11px", fontWeight: 500 }}>Auto-analyze uploaded results</span>
											</Checkbox>
										</Space>
									</div>
								</div>
							</Space>
						),
					},
					// File Restrictions - only show for free-form uploads
					...(!isFixedFormatUpload
						? [
								{
									key: "2",
									label: (
										<Space size={4}>
											<UploadOutlined style={{ fontSize: "11px" }} />
											<span style={{ fontSize: "11px", fontWeight: 500 }}>File Restrictions</span>
											{resultConfig.file_restrictions && <Badge count={`${resultConfig.file_restrictions.allowed_extensions?.length || 0} types`} style={{ backgroundColor: "#1890ff", fontSize: "10px" }} />}
										</Space>
									),
									children: (
										<Space direction="vertical" size="middle" style={{ width: "100%" }}>
											<div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
												<div>
													<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Allowed File Extensions</label>
													<Select
														mode="tags"
														value={resultConfig.file_restrictions?.allowed_extensions || []}
														onChange={(value) =>
															updateResultConfig({
																file_restrictions: {
																	...resultConfig.file_restrictions,
																	allowed_extensions: value,
																	max_file_size: resultConfig.file_restrictions?.max_file_size ?? 10,
																},
															})
														}
														placeholder="Enter file extensions"
														size="small"
														style={{ width: "100%", marginBottom: 8 }}
														disabled={disabled}
													/>
													<div style={{ fontSize: "10px", color: "var(--text-color-secondary)" }}>Press Enter after each extension</div>

													{/* File Type Presets */}
													<div style={{ marginTop: 12 }}>
														<span style={{ fontSize: "11px", fontWeight: 500, color: "var(--text-color-secondary)", display: "block", marginBottom: 8 }}>Quick Presets:</span>
														<Space size={4} wrap>
															{fileTypePresets.map((preset, index) => (
																<Button
																	key={index}
																	size="small"
																	onClick={() => applyFileTypePreset(preset)}
																	disabled={disabled}
																	title={`${preset.description} (${preset.extensions.join(", ")})`}
																	style={{ fontSize: "10px", padding: "0 8px", height: 24 }}
																>
																	{preset.name}
																</Button>
															))}
														</Space>
													</div>
												</div>

												<div>
													<Space direction="vertical" size={12} style={{ width: "100%" }}>
														<div>
															<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Max File Size (MB)</label>
															<InputNumber
																value={resultConfig.file_restrictions?.max_file_size || 10}
																onChange={(value) =>
																	updateResultConfig({
																		file_restrictions: {
																			...resultConfig.file_restrictions,
																			allowed_extensions: resultConfig.file_restrictions?.allowed_extensions ?? [],
																			max_file_size: value || 10,
																		},
																	})
																}
																min={1}
																max={1000}
																size="small"
																style={{ width: "100%" }}
																disabled={disabled}
															/>
														</div>

														<div>
															<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Min Files</label>
															<InputNumber
																value={resultConfig.file_restrictions?.min_files || 1}
																onChange={(value) =>
																	updateResultConfig({
																		file_restrictions: {
																			...resultConfig.file_restrictions,
																			allowed_extensions: resultConfig.file_restrictions?.allowed_extensions ?? [],
																			max_file_size: resultConfig.file_restrictions?.max_file_size ?? 10,
																			max_files: resultConfig.file_restrictions?.max_files ?? 10,
																			min_files: value || 1,
																		},
																	})
																}
																min={0}
																max={100}
																size="small"
																style={{ width: "100%" }}
																disabled={disabled}
															/>
														</div>

														<div>
															<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Max Files</label>
															<InputNumber
																value={resultConfig.file_restrictions?.max_files || 10}
																onChange={(value) =>
																	updateResultConfig({
																		file_restrictions: {
																			...resultConfig.file_restrictions,
																			allowed_extensions: resultConfig.file_restrictions?.allowed_extensions ?? [],
																			max_file_size: resultConfig.file_restrictions?.max_file_size ?? 10,
																			min_files: resultConfig.file_restrictions?.min_files ?? 1,
																			max_files: value || 10,
																		},
																	})
																}
																min={1}
																max={100}
																size="small"
																style={{ width: "100%" }}
																disabled={disabled}
															/>
														</div>
													</Space>
												</div>
											</div>
										</Space>
									),
								},
						  ]
						: []),
					// Custom Fields - only show for free-form uploads
					...(!isFixedFormatUpload
						? [
								{
									key: "3",
									label: (
										<Space size={4}>
											<DatabaseOutlined style={{ fontSize: "11px" }} />
											<span style={{ fontSize: "11px", fontWeight: 500 }}>Custom Result Fields</span>
											{(resultConfig.custom_fields?.length || 0) > 0 && <Badge count={resultConfig.custom_fields?.length} style={{ backgroundColor: "#8c8c8c", fontSize: "10px" }} />}
										</Space>
									),
									children: (
										<div>
											<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
												<p style={{ fontSize: "10px", color: "var(--text-color-secondary)", margin: 0 }}>Add custom fields to capture additional result information</p>
												<Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddCustomField} disabled={disabled}>
													Add Field
												</Button>
											</div>

											{(resultConfig.custom_fields?.length || 0) > 0 ? (
												<Table columns={customFieldColumns} dataSource={resultConfig.custom_fields} pagination={false} size="small" rowKey="name" />
											) : (
												<div
													style={{
														textAlign: "center",
														padding: "24px 16px",
														border: "1px solid var(--border-color)",
														borderRadius: "8px",
														backgroundColor: "var(--background-color)",
													}}
												>
													<DatabaseOutlined style={{ fontSize: "24px", color: "var(--text-color-secondary)", marginBottom: 8 }} />
													<p style={{ fontSize: "10px", color: "var(--text-color-secondary)", margin: 0 }}>No custom fields defined</p>
												</div>
											)}
										</div>
									),
								},
						  ]
						: []),
				]}
			/>

			{/* Custom Field Editor Drawer */}
			<Drawer
				title="Add Custom Field"
				open={customFieldDrawerVisible}
				onClose={() => setCustomFieldDrawerVisible(false)}
				width={400}
				footer={
					<Space size="small" style={{ display: "flex", justifyContent: "flex-end" }}>
						<Button icon={<CloseOutlined />} onClick={() => setCustomFieldDrawerVisible(false)} size="small">
							Cancel
						</Button>
						<Button type="primary" icon={<SaveOutlined />} onClick={handleSaveCustomField} disabled={!editingCustomField.name.trim()} size="small">
							Add Field
						</Button>
					</Space>
				}
			>
				<Space direction="vertical" size="middle" style={{ width: "100%" }}>
					<div>
						<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Field Name *</label>
						<Input
							value={editingCustomField.name}
							onChange={(e) =>
								setEditingCustomField({
									...editingCustomField,
									name: e.target.value,
								})
							}
							placeholder="e.g., confidence_score"
							size="small"
						/>
					</div>

					<div>
						<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Field Type *</label>
						<Select
							value={editingCustomField.type}
							options={customFieldTypes}
							onChange={(value) =>
								setEditingCustomField({
									...editingCustomField,
									type: value,
								})
							}
							size="small"
							style={{ width: "100%" }}
						/>
					</div>

					{editingCustomField.type === "dropdown" && (
						<div>
							<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Options</label>
							<Select
								mode="tags"
								value={editingCustomField.options || []}
								onChange={(value) =>
									setEditingCustomField({
										...editingCustomField,
										options: value || [],
									})
								}
								placeholder="Enter options"
								size="small"
								style={{ width: "100%" }}
							/>
							<div style={{ fontSize: "10px", color: "var(--text-color-secondary)", marginTop: 4 }}>Press Enter after each option</div>
						</div>
					)}

					<Checkbox
						checked={editingCustomField.required}
						onChange={(e) =>
							setEditingCustomField({
								...editingCustomField,
								required: e.target.checked,
							})
						}
					>
						<span style={{ fontSize: "11px" }}>Required field</span>
					</Checkbox>
				</Space>
			</Drawer>
		</div>
	);
};

export default StepResultsEditor;
